import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  query,
  where,
  updateDoc
} from "firebase/firestore";

export default function AccountsList() {
  const [accounts, setAccounts] = useState([]);
  const [openGroup, setOpenGroup] = useState(null);
  const [openBreached, setOpenBreached] = useState(false);
  const [dailyPnLMap, setDailyPnLMap] = useState({});
  const [lifetimePnLMap, setLifetimePnLMap] = useState({});

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      ::-webkit-scrollbar { width: 6px; height: 6px; }
      ::-webkit-scrollbar-track { background: #020617; }
      ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 10px; }
      ::-webkit-scrollbar-thumb:hover { background: #3b82f6; }
      body { margin: 0; padding: 0; overflow-x: hidden; background-color: #020617; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    if (!auth.currentUser) return;
    const accountsRef = collection(db, "users", auth.currentUser.uid, "accounts");
    const unsubAccounts = onSnapshot(accountsRef, (snap) => {
      const accs = snap.docs.map((d) => ({
        id: d.id,
        isBreached: d.data().isBreached || false,
        ...d.data(),
      }));
      setAccounts(accs);
      accs.forEach((acc) => {
        const tradesRef = collection(db, "users", auth.currentUser.uid, "accounts", acc.id, "trades");
        onSnapshot(tradesRef, (tSnap) => {
          const total = tSnap.docs.reduce((sum, d) => sum + Number(d.data().pnl || 0), 0);
          setLifetimePnLMap((prev) => ({ ...prev, [acc.id]: total }));
        });
        const dailyQ = query(tradesRef, where("date", "==", today));
        onSnapshot(dailyQ, (tSnap) => {
          const daily = tSnap.docs.reduce((sum, d) => sum + Number(d.data().pnl || 0), 0);
          setDailyPnLMap((prev) => ({ ...prev, [acc.id]: daily }));
        });
      });
    });
    return () => unsubAccounts();
  }, [today]);

  /* ================= CALCULATE DIVERSION STATS ================= */
  const portfolioStats = useMemo(() => {
    let totalSize = 0;
    let totalPnL = 0;
    let propCount = 0;
    let personalCount = 0;
    
    // For Equity Diversion per Phase
    let phaseEquityMap = {}; 
    let phaseCountMap = {};

    accounts.forEach(acc => {
      if (!acc.isBreached) {
        const pnl = lifetimePnLMap[acc.id] || 0;
        const size = Number(acc.size || 0);
        const equity = size + pnl;
        const phase = acc.phase || "Unknown";

        totalSize += size;
        totalPnL += pnl;
        
        // Type Counts
        if (acc.accountType === 'personal') personalCount++;
        else propCount++;

        // Phase Diversion (Equity & Count)
        phaseEquityMap[phase] = (phaseEquityMap[phase] || 0) + equity;
        phaseCountMap[phase] = (phaseCountMap[phase] || 0) + 1;
      }
    });

    return {
      totalEquity: totalSize + totalPnL,
      totalPnL,
      propCount,
      personalCount,
      phaseEquityMap,
      phaseCountMap,
      percentage: totalSize > 0 ? (totalPnL / totalSize) * 100 : 0
    };
  }, [accounts, lifetimePnLMap]);

  const toggleBreach = async (acc) => {
    await updateDoc(doc(db, "users", auth.currentUser.uid, "accounts", acc.id), {
      isBreached: !acc.isBreached,
    });
  };

  const deleteAccount = async (id) => {
    if (window.confirm("Delete account?")) {
      await deleteDoc(doc(db, "users", auth.currentUser.uid, "accounts", id));
    }
  };

  const getAccountStats = (acc) => {
    const size = Number(acc.size) || 0;
    const pnl = lifetimePnLMap[acc.id] || 0;
    const todayPnL = dailyPnLMap[acc.id] || 0;
    const dailyLimit = (size * (Number(acc.maxDailyDD) || 0)) / 100;
    const usage = dailyLimit > 0 ? Math.min(100, Math.max(0, (Math.abs(Math.min(0, todayPnL)) / dailyLimit) * 100)) : 0;
    
    let status = { label: "SAFE", color: "#22c55e" };
    if (dailyLimit + todayPnL <= 0) status = { label: "CRITICAL", color: "#ef4444" };
    else if (dailyLimit + todayPnL <= dailyLimit * 0.3) status = { label: "WARNING", color: "#facc15" };

    return { pnl, status, usage };
  };

  const renderCards = (list, isBreachedSection = false) => (
    <div style={styles.cardsGrid}>
      {list.map((acc) => {
        const { pnl, status, usage } = getAccountStats(acc);
        return (
          <div key={acc.id} style={{ ...styles.card, opacity: isBreachedSection ? 0.6 : 1, borderTop: `3px solid ${isBreachedSection ? "#334155" : status.color}` }}>
            <div style={styles.cardTop}>
              <h4 style={styles.cardSize}>{acc.currency || "$"}{(Number(acc.size) || 0).toLocaleString()}</h4>
              <span style={{ ...styles.statusBadge, color: isBreachedSection ? "#9ca3af" : status.color, border: `1px solid ${isBreachedSection ? "#334155" : status.color}30` }}>
                {isBreachedSection ? "BREACHED" : status.label}
              </span>
            </div>
            <div style={styles.infoSection}>
              <div style={row}><span>Account ID</span><span style={styles.val}>{acc.accountCode || "—"}</span></div>
              <div style={row}>
                <span>Current PnL</span>
                <span style={{ ...styles.val, color: pnl >= 0 ? "#22c55e" : "#ef4444" }}>
                  {pnl >= 0 ? "+" : ""}{acc.currency || "$"}{pnl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            {!isBreachedSection && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ ...row, fontSize: '11px', marginBottom: '4px' }}>
                  <span>Daily DD Usage</span>
                  <span>{usage.toFixed(1)}%</span>
                </div>
                <div style={styles.progressBg}><div style={{ ...styles.progressFill, width: `${usage}%`, backgroundColor: status.color }} /></div>
              </div>
            )}
            <div style={styles.cardActions}>
              <button style={styles.secondaryBtn} onClick={() => toggleBreach(acc)}>{acc.isBreached ? "Restore" : "Breach"}</button>
              <button style={styles.dangerBtn} onClick={() => deleteAccount(acc.id)}>Delete</button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const propGrouped = {};
  const personalGrouped = {};
  const breachedList = [];

  accounts.forEach(acc => {
    if (acc.isBreached) {
      breachedList.push(acc);
    } else {
      const broker = acc.name || "Unknown Broker";
      const phase = acc.phase || "Unknown Phase";
      const targetGroup = acc.accountType === "personal" ? personalGrouped : propGrouped;

      if (!targetGroup[broker]) targetGroup[broker] = {};
      if (!targetGroup[broker][phase]) targetGroup[broker][phase] = [];
      targetGroup[broker][phase].push(acc);
    }
  });

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h2 style={styles.pageTitle}>Your Trading Portfolio</h2>
      </header>

      {/* ================= UPDATED DIVERSION SUMMARY BAR ================= */}
      <div style={styles.summaryContainer}>
        <div style={styles.portfolioBar}>
          <div style={styles.barItem}>
            <div style={{ ...styles.pip, background: '#3b82f6' }} />
            <span style={styles.barLabel}>TOTAL EQUITY:</span>
            <span style={styles.barValue}>${portfolioStats.totalEquity.toLocaleString()}</span>
          </div>
          <div style={styles.barItem}>
            <div style={{ ...styles.pip, background: portfolioStats.totalPnL >= 0 ? '#22c55e' : '#ef4444' }} />
            <span style={styles.barLabel}>NET PNL:</span>
            <span style={{ ...styles.barValue, color: portfolioStats.totalPnL >= 0 ? '#22c55e' : '#ef4444' }}>
              {portfolioStats.totalPnL >= 0 ? '+' : ''}{portfolioStats.percentage.toFixed(2)}%
            </span>
          </div>
          <div style={styles.barItem}>
            <div style={{ ...styles.pip, background: '#f59e0b' }} />
            <span style={styles.barLabel}>PROP:</span>
            <span style={styles.barValue}>{portfolioStats.propCount}</span>
          </div>
          <div style={styles.barItem}>
            <div style={{ ...styles.pip, background: '#ec4899' }} />
            <span style={styles.barLabel}>PERSONAL:</span>
            <span style={styles.barValue}>{portfolioStats.personalCount}</span>
          </div>
        </div>

        {/* EQUITY DIVERSION PER PHASE */}
        <div style={styles.phaseBar}>
          <span style={styles.phaseTitle}>EQUITY DIVERSION:</span>
          {Object.entries(portfolioStats.phaseEquityMap).map(([phase, equity]) => (
            <div key={phase} style={styles.phaseBadge}>
              <span style={styles.phaseName}>{phase.toUpperCase()}</span>
              <span style={styles.phaseValue}>${equity.toLocaleString()}</span>
              <span style={styles.phaseCount}>({portfolioStats.phaseCountMap[phase]})</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* SECTION 1: PROP FIRMS */}
      <div style={styles.sectionLabel}>PROP FIRM ACCOUNTS</div>
      {Object.entries(propGrouped).map(([broker, phases]) => (
        <AccordionItem 
          key={`prop-${broker}`} 
          title={broker} 
          count={Object.values(phases).flat().length} 
          isOpen={openGroup === `prop-${broker}`} 
          onToggle={() => setOpenGroup(openGroup === `prop-${broker}` ? null : `prop-${broker}`)}
        >
          {Object.entries(phases).map(([phaseName, list]) => (
            <div key={phaseName} style={styles.phaseContainer}>
              <div style={styles.phaseLabel}>{phaseName.toUpperCase()} ({list.length})</div>
              {renderCards(list)}
            </div>
          ))}
        </AccordionItem>
      ))}

      {/* SECTION 2: PERSONAL */}
      {Object.keys(personalGrouped).length > 0 && (
        <>
          <div style={{...styles.sectionLabel, marginTop: '30px'}}>PERSONAL ACCOUNTS</div>
          {Object.entries(personalGrouped).map(([broker, phases]) => (
            <AccordionItem 
              key={`pers-${broker}`} 
              title={broker} 
              count={Object.values(phases).flat().length} 
              isOpen={openGroup === `pers-${broker}`} 
              onToggle={() => setOpenGroup(openGroup === `pers-${broker}` ? null : `pers-${broker}`)}
            >
              {Object.entries(phases).map(([phaseName, list]) => (
                <div key={phaseName} style={styles.phaseContainer}>
                  <div style={styles.phaseLabel}>{phaseName.toUpperCase()} ({list.length})</div>
                  {renderCards(list)}
                </div>
              ))}
            </AccordionItem>
          ))}
        </>
      )}

      {/* SECTION 3: BREACHED */}
      {breachedList.length > 0 && (
        <>
          <div style={{...styles.sectionLabel, marginTop: '30px', color: '#ef4444'}}>BREACHED ACCOUNTS</div>
          <AccordionItem 
            title="Archives" 
            count={breachedList.length} 
            isOpen={openBreached} 
            onToggle={() => setOpenBreached(!openBreached)} 
            isDanger
          >
            {renderCards(breachedList, true)}
          </AccordionItem>
        </>
      )}
    </div>
  );
}

const AccordionItem = ({ title, count, isOpen, onToggle, children, isDanger }) => (
  <div style={styles.accordion}>
    <div style={{...styles.accordionHeader, borderLeft: isDanger ? '4px solid #ef4444' : '4px solid #3b82f6' }} onClick={onToggle}>
      <span style={{ fontWeight: '800', fontSize: '13px', color: '#fff', letterSpacing: '0.5px' }}>
        {title.toUpperCase()} <span style={styles.countBadge}>{count}</span>
      </span>
      <span style={{ fontSize: '10px', fontWeight: '900', color: isOpen ? '#3b82f6' : '#475569' }}>
        {isOpen ? "CLOSE ▲" : "OPEN ▼"}
      </span>
    </div>
    {isOpen && <div style={{ width: '100%', background: '#05070a' }}>{children}</div>}
  </div>
);

const styles = {
  container: { width: "100%", padding: "20px", boxSizing: "border-box" },
  header: { marginBottom: "20px" },
  pageTitle: { fontSize: "24px", fontWeight: "900", color: '#fff', margin: 0 },
  
  /* SUMMARY CONTAINER */
  summaryContainer: {
    background: '#0d1117',
    borderRadius: '12px',
    border: '1px solid #1e293b',
    marginBottom: '30px',
    overflow: 'hidden'
  },
  portfolioBar: {
    display: 'flex',
    gap: '30px',
    padding: '16px 24px',
    borderBottom: '1px solid #1e293b',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  phaseBar: {
    display: 'flex',
    gap: '12px',
    padding: '10px 24px',
    background: '#0a0d12',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  barItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  pip: { width: '3px', height: '14px', borderRadius: '2px' },
  barLabel: { fontSize: '9px', fontWeight: '900', color: '#475569', letterSpacing: '1px' },
  barValue: { fontSize: '14px', fontWeight: '800', color: '#fff' },

  /* PHASE DIVERSION BADGES */
  phaseTitle: { fontSize: '9px', fontWeight: '900', color: '#334155', letterSpacing: '1px', marginRight: '5px' },
  phaseBadge: { 
    display: 'flex', 
    alignItems: 'center', 
    gap: '6px', 
    background: 'rgba(30, 41, 59, 0.4)', 
    padding: '3px 12px', 
    borderRadius: '6px',
    border: '1px solid #1e293b'
  },
  phaseName: { fontSize: '9px', fontWeight: '900', color: '#94a3b8' },
  phaseValue: { fontSize: '11px', fontWeight: '800', color: '#22c55e' },
  phaseCount: { fontSize: '9px', fontWeight: '700', color: '#3b82f6' },

  sectionLabel: { fontSize: '10px', fontWeight: '900', color: '#475569', letterSpacing: '2px', marginBottom: '12px' },
  accordion: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "8px", overflow: "hidden", marginBottom: "12px" },
  accordionHeader: { padding: "16px 20px", display: "flex", justifyContent: "space-between", cursor: "pointer", alignItems: "center", background: "#0d1117" },
  countBadge: { marginLeft: "8px", fontSize: "10px", background: "#1e293b", padding: "2px 8px", borderRadius: "4px", color: "#3b82f6" },
  
  phaseContainer: { padding: "10px 20px 20px 20px", borderBottom: "1px solid #1e293b44" },
  phaseLabel: { fontSize: '10px', fontWeight: '800', color: '#3b82f6', marginBottom: '15px', paddingLeft: '10px', borderLeft: '2px solid #3b82f6' },
  
  cardsGrid: { 
    display: "grid", 
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", 
    gap: "16px", 
    width: "100%",
    boxSizing: "border-box"
  },
  card: { background: "#0d1117", border: "1px solid #1e293b", borderRadius: "10px", padding: "16px", display: 'flex', flexDirection: 'column' },
  cardTop: { display: "flex", justifyContent: "space-between", marginBottom: "12px", alignItems: "center" },
  cardSize: { fontSize: "16px", fontWeight: "800", margin: 0, color: '#fff' },
  statusBadge: { fontSize: "8px", padding: "2px 6px", borderRadius: "4px", fontWeight: "800" },
  infoSection: { display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" },
  val: { fontWeight: "600", color: "#f8fafc" },
  progressBg: { width: '100%', height: '4px', background: '#1e293b', borderRadius: '10px' },
  progressFill: { height: '100%', borderRadius: '10px', transition: 'width 0.4s ease' },
  cardActions: { display: "flex", gap: "8px", marginTop: 'auto' },
  secondaryBtn: { flex: 2, background: "#1e293b", border: "1px solid #334155", color: "#facc15", padding: "6px", borderRadius: "6px", cursor: "pointer", fontSize: "10px", fontWeight: '700' },
  dangerBtn: { flex: 1, background: "transparent", border: "1px solid #450a0a", color: "#ef4444", padding: "6px", borderRadius: "6px", cursor: "pointer", fontSize: "10px", fontWeight: '700' }
};

const row = { display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#94a3b8" };